import type { NavigateFunction } from 'react-router-dom';
import { create } from 'zustand';

import { historyIdx } from '@/hooks/useNavigateUp';

type HandlerRef = { current: (() => void | Promise<void>) | undefined };

type BackHandlerState = {
  stack: Array<{ id: number; ref: HandlerRef }>;
  push: (ref: HandlerRef) => number;
  pop: (id: number) => void;
};

let nextId = 0;

export const useBackHandlerStore = create<BackHandlerState>((set) => ({
  stack: [],
  push(ref) {
    const id = ++nextId;
    set((s) => ({ stack: [...s.stack, { id, ref }] }));
    return id;
  },
  pop(id) {
    set((s) => ({ stack: s.stack.filter((e) => e.id !== id) }));
  },
}));

/** Read the active back handler without subscribing to store updates. */
export const peekBackHandler = (): (() => void | Promise<void>) | undefined => {
  const { stack } = useBackHandlerStore.getState();
  return stack.length > 0 ? stack[stack.length - 1].ref.current : undefined;
};

/**
 * Where back goes from here, for a swipe (EdgeSwipeBack) or the iPhone Duo rail's button
 * (Layout): the active handler, else a nested page with no back button of its own (Manage ›
 * Budgets, Inbox › Contacts) goes back the way the browser would. A tab's root (one path
 * segment) does not, and neither does a page opened directly with nothing in the app behind it.
 */
export const getBackHandler = (navigate: NavigateFunction) => {
  const handler = peekBackHandler();
  if (handler) return handler;
  const nested = window.location.pathname.split('/').filter(Boolean).length > 1;
  return nested && historyIdx() > 0 ? () => navigate(-1) : undefined;
};
